import { BasicButton, SecondCustomInput } from "@/components";
import useProfile from "@/hooks/useProfile";
import { isAuthenticating } from "@/libraries/helper";
import { postRegister, preSignAvatar } from "@/redux/reducers/auth";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

export const Route = createFileRoute("/create-account/professional-profile")({
	beforeLoad: () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/create-account",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["auth", "common"]);
	const dispatch = useDispatch();
	const navigate = Route.useNavigate();
	// initials form credentials
	const [formCredentials, setFormCredentials] = useReducer(
		(state: any, newState: any) => ({ ...state, ...newState }),
		{
			file_id: "",
			job_title: "",
			qualification: "",
			spesialization: "",
		},
	);
	const { profile: userData } = useProfile();
	const message = useSelector((state: any) => state.auth?.postRegister);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [avatarPreview, setAvatarPreview] = useState(
		"/images/blank-avatar.svg",
	);
	// button disable
	const [buttonDisable, setButtonDisable] = useState(true);
	const [directory, setDirectory] = useState<true | false | null>(null);
	const [loading, setLoading] = useState(false);

	// setFormCredentials from localStorage
	useEffect(() => {
		const savedFormCredentials = localStorage.getItem("formCredentials");
		if (savedFormCredentials) {
			setFormCredentials(JSON.parse(savedFormCredentials));
		}
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (message?.status === "failed") {
			setLoading(false);
		}
		// check input value
		setButtonDisable(directory === null);
	}, [directory, message, userData]);

	const handleSubmit = useCallback(() => {
		setLoading(true);
		dispatch(
			postRegister({
				id: userData?.id,
				job_title: formCredentials.job_title,
				spesialization: formCredentials.spesialization,
				qualification: formCredentials.qualification,
				directory: directory,
				callback: () => {
					setLoading(false);
					localStorage.setItem(
						"formCredentials",
						JSON.stringify(formCredentials),
					);
					navigate({
						to: "/create-account/cookie-credentials",
					});
				},
			}),
		);
	}, [dispatch, userData, formCredentials, directory, navigate]);

	const handleFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (!userData) return;
			const file = event.target.files?.item(0);
			if (file) {
				setAvatarPreview(URL.createObjectURL(file)); // Update the avatar preview
				dispatch(preSignAvatar({ userId: userData.id, file }));
			}
		},
		[dispatch, userData],
	);

	useEffect(() => {
		if (userData?.avatar) {
			setAvatarPreview(userData.avatar);
		}
	}, [userData?.avatar]);

	return (
		<>
			<div className="py-4 relative container flex-1 overflow-y-auto">
				<div className="bg-gray-light flex items-start gap-3 py-3 mx-auto">
					<InfoRoundedIcon className="!text-base text-gray-200" />
					<p className="text-dark text-xs">
						{t("auth:professionalProfile.skipHint")}
					</p>
				</div>
				<div className="mx-auto mb-4">
					<div className="flex justify-center items-center my-4">
						{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
						<div
							onClick={() => fileInputRef.current?.click()}
							className="relative w-24 h-24"
						>
							<div className="h-full w-full rounded-full overflow-hidden bg-gray-300">
								<img
									className="h-full w-full object-cover"
									src={avatarPreview}
									alt="avatar"
								/>
							</div>
							<div className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center p-2">
								<input
									aria-label="file"
									type="file"
									ref={fileInputRef}
									onChange={handleFileChange}
									className="hidden"
								/>
								<CameraAltRoundedIcon className="text-gray-600" />
							</div>
						</div>
					</div>
					<p className="text-dark text-base text-center font-bold mb-4">
						{userData?.fullname ?? "-"}
					</p>
					<div className="bg-gray-light rounded-lg py-3 px-4 mb-4">
						{userData?.profession && (
							<p className="text-dark text-base mb-2">{userData?.profession}</p>
						)}
						{userData?.hospital && (
							<p className="text-dark text-base mb-2">
								{userData?.hospital?.name}
							</p>
						)}
						{userData?.registration_number && (
							<p className="text-dark text-base mb-2">
								{t("auth:professionalProfile.registrationNo", {
									number: userData?.registration_number,
								})}
							</p>
						)}
					</div>
					<p className="text-dark text-sm font-bold py-2 mb-2">
						{t("auth:professionalProfile.additionalInfo")}{" "}
						<span>{t("auth:professionalProfile.orFillLater")}</span>
					</p>
					<div className="mb-6">
						<SecondCustomInput
							type="text"
							label={t("auth:professionalProfile.jobTitle")}
							name="job"
							value={formCredentials.job_title}
							onChange={(e) =>
								setFormCredentials({ job_title: e.target.value })
							}
						/>
					</div>
					<div className="mb-6">
						<SecondCustomInput
							type="text"
							label={t("auth:professionalProfile.qualifications")}
							name="qualifications"
							value={formCredentials.qualification}
							onChange={(e) =>
								setFormCredentials({ qualification: e.target.value })
							}
						/>
					</div>
					<SecondCustomInput
						type="text"
						label={t("auth:professionalProfile.specialisation")}
						name="specialisation"
						value={formCredentials.spesialization}
						onChange={(e) =>
							setFormCredentials({ spesialization: e.target.value })
						}
					/>
				</div>
				<div className="py-2 mx-auto border-t border-solid border-light-gray">
					<p className="text-dark text-sm font-bold py-2">
						{t("auth:professionalProfile.addToDirectory")}{" "}
						<span>{t("auth:professionalProfile.mandatory")}</span>
					</p>
					<div className="flex items-center my-2 gap-2">
						<BasicButton
							variant="outlined"
							color={directory === true ? "info" : "inherit"}
							onClick={() => setDirectory(true)}
							className="w-full !h-12 !text-base font-medium !shadow-none"
						>
							<div className="flex items-center gap-2">
								<CheckIcon />
								<p>{t("auth:professionalProfile.yes")}</p>
							</div>
						</BasicButton>
						<BasicButton
							variant="outlined"
							color={directory === false ? "error" : "inherit"}
							onClick={() => setDirectory(false)}
							className="w-full !h-12 !text-base font-medium !shadow-none"
						>
							<div className="flex items-center gap-2">
								<CloseIcon />
								<p>{t("auth:professionalProfile.no")}</p>
							</div>
						</BasicButton>
					</div>
					{directory === true && (
						<div className="flex items-start gap-1">
							<InfoRoundedIcon className="!text-base" />
							<p className="text-dark text-xs font-medium">
								{t("auth:professionalProfile.directoryYesHint")}
							</p>
						</div>
					)}
					{directory === false && (
						<div className="flex items-start gap-1">
							<InfoRoundedIcon className="!text-base" />
							<p className="text-dark text-xs font-medium">
								{t("auth:professionalProfile.directoryNoHint")}
							</p>
						</div>
					)}
				</div>
			</div>
			<div className="container py-4 shadow">
				<BasicButton
					variant="contained"
					color="secondary"
					onClick={handleSubmit}
					disabled={buttonDisable}
					loading={loading}
					className="w-full !h-12 !text-sm semibold !shadow-none"
				>
					{t("auth:professionalProfile.next")}
				</BasicButton>
			</div>
		</>
	);
}
